import { QueryHookOptions } from '@apollo/client';
import { groupBy } from 'lodash';

import { EntityDetailSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { AiSuggestionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  FetchEntityAiSuggestionsV2Query,
  FetchEntityAiSuggestionsV2QueryVariables,
  useFetchEntityAiSuggestionsV2Query,
} from '../graphql/aiSuggestionsV2.generated';

type V2QueryOptions = Omit<
  QueryHookOptions<
    FetchEntityAiSuggestionsV2Query,
    FetchEntityAiSuggestionsV2QueryVariables
  >,
  'variables'
>;

interface UseFetchEntityAISuggestionsOptions {
  entityId: string | undefined | null;
  skip?: boolean;
  onError?: V2QueryOptions['onError'];
  onCompleted?: (data: FetchEntityAiSuggestionsV2Query) => void;
}

/** Fetches entity-detail suggestions and accepted legacy evidence. */
export function useFetchEntityAISuggestions({
  entityId,
  skip,
  onError,
  onCompleted,
}: UseFetchEntityAISuggestionsOptions) {
  return useFetchEntityAiSuggestionsV2Query({
    skip: skip || !entityId,
    variables: {
      entityID: entityId!,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onError,
    onCompleted,
  });
}

export interface ProcessedSuggestionsData {
  suggestions: EntityDetailSuggestion[];
  groupedByKind: Record<AiSuggestionKind, EntityDetailSuggestion[]>;
}

/** Groups suggestions by kind, including accepted suggestions for evidence. */
export function processSuggestionsDataV2(
  data: FetchEntityAiSuggestionsV2Query
): ProcessedSuggestionsData {
  const suggestions = getNodes(data.aiSuggestions) as EntityDetailSuggestion[];

  const groupedByKind = groupBy(suggestions, (s) => s.kind) as Record<
    AiSuggestionKind,
    EntityDetailSuggestion[]
  >;

  return { suggestions, groupedByKind };
}
